import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "sales-report-new-chart__title" };
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import i18n from '@/i18n';
// utils
import { formatCurrency } from '@/utilities/formatters';
// components
import BaseLoader from '@/components/_atoms/BaseLoaderSpinner.vue';
import InfoCard from '@/components/_atoms/InfoCard.vue';
export default /*#__PURE__*/ _defineComponent({
    __name: 'SalesReportNewGraphic',
    setup(__props) {
        // libs
        const store = useStore();
        // computed
        const graphic = computed(() => store.getters['salesReportNew/getGraphic']);
        const isLoading = computed(() => store.getters['salesReportNew/getIsIndicatorsLoading']);
        const hasError = computed(() => store.getters['salesReportNew/getHasIndicatorsError']);
        const hasData = computed(() => store.getters['salesReportNew/getHasDataOnGraphic']);
        const seriesGraphic = computed(() => [
            {
                name: i18n.global.t('campaign.sales-report.graphic.attributed-revenue'),
                type: 'column',
                color: '#FF3162',
                data: graphic?.value?.metrics?.revenue,
                marker: { symbol: 'circle' },
            },
            {
                name: i18n.global.t('campaign.sales-report.graphic.sales'),
                type: 'line',
                color: '#6C90F4',
                data: graphic?.value?.metrics?.totalSales,
                marker: { symbol: 'line' },
                yAxis: 1,
            },
        ]);
        // actions
        const fetchIndicators = () => store.dispatch('salesReportNew/setFetchIndicators');
        // computed
        const getDatepickerDates = computed(() => store.getters['dates']);
        const maxLeftYAxis = computed(() => {
            return Math.max(...seriesGraphic?.value[0]?.data);
        });
        const maxRightYAxis = computed(() => {
            return Math.max(...seriesGraphic?.value[1]?.data);
        });
        const getChartOptions = computed(() => ({
            chart: {
                zoomType: 'xy',
                height: 400,
                events: {
                    load: function () {
                        document.querySelectorAll('.highcharts-legend-item path').forEach(function (element) {
                            element.setAttribute('stroke-width', '5');
                        });
                    },
                },
                style: {
                    fontFamily: 'Montserrat',
                    margin: '0 auto',
                },
            },
            title: {
                text: null,
            },
            legend: {
                itemMarginTop: -30,
                itemMarginBottom: 35,
                itemDistance: 40,
                enabled: true,
                align: 'left',
                verticalAlign: 'top',
                itemStyle: {
                    color: '#555',
                    fontWeight: 600,
                },
            },
            tooltip: {
                shared: true,
                useHTML: true,
                borderWidth: 0,
                backgroundColor: '#fff',
                padding: 14,
                formatter: function formater() {
                    const revenue = i18n.global.t('campaign.sales-report.graphic.attributed-revenue');
                    const htmlTooltip = this.points.map(info => {
                        if (info.series.name === revenue) {
                            return `<div><b>${info.key}</b><div style="display: flex; align-items: center; margin-bottom: 5px; margin-top: 8px"><div style="color:${info.color}; margin-right: 5px; height: 10px; width: 10px ;background-color:${info.color}; border-radius: 50%"></div>${info.series.name}: <b style="margin-left: 3px">${formatCurrency(info.y)}</b></div> </div>`;
                        }
                        else {
                            return `<div style="display: flex; align-items: center; margin-bottom: 5px"><div style="color:${info.color}; margin-right: 5px; height: 5px; width: 10px ;background-color:${info.color} "></div><span>${info.series.name}</span>: <b style="margin-left: 3px">${info.y.toString().replace('.', ',')}</b></div> `;
                        }
                    });
                    return htmlTooltip;
                },
            },
            plotOptions: {
                series: {
                    marker: { enabled: false },
                },
                column: {
                    stacking: 'normal',
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                },
            },
            credits: {
                enabled: false,
            },
            xAxis: {
                labels: {
                    rotation: -45,
                },
                categories: graphic?.value?.labels?.map((date) => {
                    return date;
                }),
                tickWidth: 0,
            },
            yAxis: [
                {
                    title: {
                        text: null,
                    },
                    max: maxLeftYAxis.value,
                    tickPositions: [
                        0,
                        maxLeftYAxis.value * 0.2,
                        maxLeftYAxis.value * 0.4,
                        maxLeftYAxis.value * 0.6,
                        maxLeftYAxis.value * 0.8,
                        maxLeftYAxis.value,
                    ],
                    lineWidth: 0,
                    labels: {
                        style: {
                            width: '60px',
                            whiteSpace: 'normal',
                        },
                        formatter: function formatter(data) {
                            return formatCurrency(data.value);
                        },
                    },
                },
                {
                    tickPositions: [
                        0,
                        maxRightYAxis.value * 0.2,
                        maxRightYAxis.value * 0.4,
                        maxRightYAxis.value * 0.6,
                        maxRightYAxis.value * 0.8,
                        maxRightYAxis.value,
                    ],
                    max: 100,
                    lineWidth: 0,
                    opposite: true,
                    title: {
                        text: null,
                    },
                    labels: {
                        style: {
                            width: '60px',
                            whiteSpace: 'normal',
                        },
                        formatter: function formatter(data) {
                            return Math.floor(data.value);
                        },
                    },
                },
            ],
            series: seriesGraphic.value,
        }));
        watch(() => getDatepickerDates, () => {
            fetchIndicators();
        }, { deep: true });
        return (_ctx, _cache) => {
            const _component_highcharts = _resolveComponent("highcharts");
            const _component_base_card_v2 = _resolveComponent("base-card-v2");
            return (_openBlock(), _createBlock(_component_base_card_v2, { class: "sales-report-new-chart" }, {
                default: _withCtx(() => [
                    (!isLoading.value && hasData.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createElementVNode("strong", _hoisted_1, _toDisplayString(_ctx.$t('campaign.sales-report.graphic.title')), 1),
                            _createVNode(_component_highcharts, {
                                options: getChartOptions.value,
                                class: "sales-report-new-chart__highcharts"
                            }, null, 8, ["options"])
                        ], 64))
                        : _createCommentVNode("", true),
                    (isLoading.value)
                        ? (_openBlock(), _createBlock(BaseLoader, { key: 1 }))
                        : _createCommentVNode("", true),
                    (!hasData.value && !isLoading.value)
                        ? (_openBlock(), _createBlock(InfoCard, {
                            key: 2,
                            ref: "message-error",
                            error: hasError.value
                        }, null, 8, ["error"]))
                        : _createCommentVNode("", true)
                ]),
                _: 1
            }));
        };
    }
});
